import { useId } from 'react';
import { Box, FlexBox, Icon, Typography } from '@vp/swan';
import styled, { keyframes } from 'styled-components';
import { __ } from '@99designs/i18n';
import { CircularProgressBar } from './CircularProgressBar';

const SIZE = 24;
const STROKE_WIDTH = 2;
const MAX_VALUE = 100;
const MIN_VALUE = 0;

const fadeInScaleUp = keyframes`
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const AnimatedIcon = styled(Icon)`
    animation: ${fadeInScaleUp} 0.5s ease-in-out;
`;

export interface Props {
    value: number /** Value from 0 to 100 inclusive. */;
}

export const ProgressIndicator = ({ value = 0 }: Props) => {
    const clampedValue = Math.min(Math.max(value, MIN_VALUE), MAX_VALUE);
    const id = useId();
    const labelId = `progress-label-${id}`;
    return (
        <FlexBox
            alignItems="center"
            justifyContent="left"
            gap="between-icon-and-text"
            role="progressbar"
            aria-valuenow={clampedValue}
            aria-valuemin={MIN_VALUE}
            aria-valuemax={MAX_VALUE}
            aria-labelledby={labelId}
        >
            {clampedValue < MAX_VALUE ? (
                <CircularProgressBar
                    value={clampedValue}
                    min={MIN_VALUE}
                    max={MAX_VALUE}
                    size={SIZE}
                    strokeWidth={STROKE_WIDTH}
                />
            ) : (
                <AnimatedIcon
                    iconType="success"
                    skin="success"
                    size="24p"
                    data-testid="complete-progress-icon"
                />
            )}
            <Box>
                <Typography id={labelId} textColor="subtle" fontSize="small">
                    {__('Assessing your style: {{percentage}}%', {
                        percentage: Math.round(clampedValue),
                        _context:
                            "Indicates the progress of determining the customer's preferred styles",
                    })}
                </Typography>
            </Box>
        </FlexBox>
    );
};
